import { getSurvivorGameplayPerkId } from './survivorPerkEquivalents'

const INTERACTION_ID_ALIASES = {
  babysitter: 'baby_sitter',
  detective_s_hunch: 'detectives_hunch',
  plunderer_s_instinct: 'plunderers_instinct'
}

// Canonical interaction contracts for perks whose value depends on the exact
// mechanic exposed by another perk. Dense tag/synergy maps remain useful as a
// fallback, but they must never overrule these contracts.

const EXHAUSTION_SPENDERS = new Set([
  'background_player',
  'balanced_landing',
  'dead_hard',
  'dramaturgy',
  'head_on',
  'lithe',
  'overcome',
  'smash_hit',
  'sprint_burst'
])

const EXHAUSTION_APPLIERS = new Set(Array.from(EXHAUSTION_SPENDERS).concat([
  'adrenaline',
  'rapid_response'
]))

const TIMED_KILLER_AURA_SOURCES = {
  alert: 94,
  baby_sitter: 86,
  breakdown: 84,
  dark_sense: 92,
  fogwise: 100,
  hardened: 90,
  inner_focus: 92,
  object_of_obsession: 88,
  poised: 88,
  rapid_response: 82,
  scene_partner: 96,
  streetwise: 78,
  strength_in_shadows: 86,
  treacherous_crows: 84,
  troubleshooter: 90,
  we_see_you: 88,
  wicked: 84
}

const NON_TIMED_AURA_SOURCES = new Set([
  'aftercare',
  'better_together',
  'blood_pact',
  'bond',
  'buckle_up',
  'clairvoyance',
  'deja_vu',
  'detectives_hunch',
  'empathy',
  'extrasensory_perception',
  'kindred',
  'lucky_star',
  'open_handed',
  'plunderers_instinct',
  'power_struggle',
  'premonition',
  'quick_gambit',
  'red_herring',
  'saboteur',
  'still_sight',
  'teamwork_throw_down',
  'visionary',
  'wake_up',
  'windows_of_opportunity',
  'wiretap'
])

const FINITE_SURVIVOR_AURA_RANGE_SOURCES = {
  bond: 96,
  clairvoyance: 82,
  detectives_hunch: 86,
  empathy: 78,
  extrasensory_perception: 100,
  kindred: 100,
  plunderers_instinct: 76,
  saboteur: 76,
  still_sight: 80,
  visionary: 78,
  wake_up: 72,
  windows_of_opportunity: 92
}

const SPRINT_BURST_PACKAGE = {
  fixated: 100,
  vigil: 98,
  windows_of_opportunity: 94,
  ghost_notes: 84,
  blood_rush: 78,
  deja_vu: 70,
  resilience: 68
}

const SPRINT_BURST_FALSE_FRIENDS = new Set([
  'cut_loose',
  'dance_with_me',
  'quick_and_quiet'
])

const NO_MITHER_HARD_CONFLICTS = {
  self_care: 'No Mither keeps the Survivor Broken, so Self-Care can never complete a heal.',
  inner_strength: 'No Mither keeps the Survivor Broken, so Inner Strength cannot deliver its heal.',
  strength_in_shadows: 'No Mither keeps the Survivor Broken, so Strength in Shadows cannot complete its self-heal.',
  second_wind: 'No Mither keeps the Survivor Broken, so Second Wind cannot restore a Health State.',
  moment_of_glory: 'No Mither keeps the Survivor Broken, so Moment of Glory cannot restore a Health State.',
  iron_will: 'No Mither already suppresses Grunts of Pain completely, so Iron Will adds no usable effect.',
  finesse: 'Finesse requires Healthy; No Mither keeps the Survivor Injured and Broken.',
  road_life: 'Road Life cannot activate while Broken; No Mither applies Broken for the entire Trial.',
  clean_break: 'Clean Break requires a completed heal and later restores Health; both parts are prevented by permanent Broken.',
  reactive_healing: 'Reactive Healing adds healing progress that cannot restore a Health State while No Mither keeps the Survivor Broken.',
  resurgence: 'Resurgence adds healing progress that cannot restore a Health State while No Mither keeps the Survivor Broken.',
  solidarity: 'Solidarity converts altruistic healing into self-healing progress that permanent Broken makes unusable.',
  dramaturgy: 'Dramaturgy requires Healthy; No Mither keeps the Survivor Injured and Broken.',
  light_footed: 'Light-Footed requires Healthy; No Mither keeps the Survivor Injured and Broken.',
  overcome: 'Overcome requires losing a Health State from Healthy; No Mither prevents that trigger pattern.',
  for_the_people: 'For the People requires Healthy; No Mither keeps the Survivor Injured and Broken.'
}

export const survivorPerkInteractionKnowledge = {
  eyes_of_belmont: {
    usage: 'Adds 2 seconds only to a timed reveal of the Killer Aura. Continuous, range-limited or condition-held Aura reading is not extended.',
    provides: ['killer_aura_duration_extension'],
    requires: ['timed_killer_aura'],
    synergies: TIMED_KILLER_AURA_SOURCES,
    nonSynergies: Array.from(NON_TIMED_AURA_SOURCES)
  },
  open_handed: {
    usage: 'Extends finite Survivor-based Aura-reading ranges. It does not extend a reveal duration, a trigger radius or an Aura emitted by an object.',
    provides: ['finite_survivor_aura_range_extension'],
    requires: ['finite_survivor_aura_range'],
    synergies: FINITE_SURVIVOR_AURA_RANGE_SOURCES
  },
  sprint_burst: {
    usage: 'Creates distance when running starts. Its best package preserves walking mobility, accelerates Exhaustion recovery and reads the next chase route.',
    provides: ['exhaustion_spender', 'run_start_burst'],
    synergies: SPRINT_BURST_PACKAGE,
    nonSynergies: Array.from(SPRINT_BURST_FALSE_FRIENDS)
  },
  no_mither: {
    usage: 'Keeps the Survivor permanently Injured and Broken. Standard self-heals and Healthy-only activations cannot deliver their main effect.',
    provides: ['permanent_broken', 'permanent_injured'],
    antiSynergies: NO_MITHER_HARD_CONFLICTS
  },
  wide_open_throttle: {
    usage: 'Fast-vaulting a Pallet immediately resets and blocks that Pallet, then grants a short Haste burst. It does not leave a dropped Pallet for Any Means Necessary to reset.',
    provides: ['pallet_reset', 'chase_reposition'],
    requires: ['fast_pallet_vault'],
    nonSynergies: ['any_means_necessary']
  }
}

function canonicalId (perkOrId) {
  const id = typeof perkOrId === 'string' ? perkOrId : perkOrId && perkOrId.id
  const normalizedId = INTERACTION_ID_ALIASES[id] || id
  return getSurvivorGameplayPerkId(normalizedId)
}

function interaction (kind, weight, reason, options) {
  return Object.assign({
    kind,
    weight,
    reason,
    bestBuildBlocked: false,
    severity: null,
    source: 'mechanic-contract'
  }, options || {})
}

function pairKey (firstId, secondId) {
  return [canonicalId(firstId), canonicalId(secondId)].sort().join('|')
}

function exactPair (firstId, secondId, kind, weight, reason, options) {
  return {
    key: pairKey(firstId, secondId),
    value: interaction(kind, weight, reason, options)
  }
}

const EXACT_PAIR_RULE_LIST = [
  exactPair('stake_out', 'hyperfocus', 'synergy', 100, 'Stake Out converts Good Repair Skill Checks into Great ones that build and preserve Hyperfocus tokens.'),
  exactPair('stake_out', 'fogwise', 'synergy', 94, 'Stake Out supplies Great Repair Skill Checks that trigger Fogwise Aura reading.'),
  exactPair('stake_out', 'fast_track', 'synergy', 92, 'Stake Out supplies the Great Repair Skill Check needed to spend Fast Track tokens reliably.'),
  exactPair('stake_out', 'scavenger', 'synergy', 90, 'Stake Out converts Repair Skill Checks into the Great results that charge Scavenger.'),
  exactPair('stake_out', 'road_life', 'synergy', 88, 'Stake Out converts Repair Skill Checks into Great results that build Road Life tokens.'),
  exactPair('stake_out', 'specialist', 'synergy', 94, 'Stake Out converts a Good Repair Skill Check into the Great result that spends Specialist tokens.'),
  exactPair('scavenger', 'hyperfocus', 'synergy', 94, 'The same Great Repair Skill Checks build Hyperfocus and charge Scavenger.'),
  exactPair('fogwise', 'hyperfocus', 'synergy', 88, 'The same Great Repair Skill Checks trigger Fogwise while building and preserving Hyperfocus tokens.'),
  exactPair('specialist', 'hyperfocus', 'synergy', 90, 'The same Great Repair Skill Check spends Specialist tokens while building or preserving Hyperfocus.'),
  exactPair('road_life', 'hyperfocus', 'synergy', 86, 'Consecutive Great Repair Skill Checks advance both Road Life and Hyperfocus.'),
  exactPair('one_two_three_four', 'autodidact', 'synergy', 92, 'One-Two-Three-Four increases Healing Skill Check frequency, accelerating Autodidact token gain.'),
  exactPair('one_two_three_four', 'fogwise', 'synergy', 86, 'One-Two-Three-Four creates more Repair Skill Check opportunities for Fogwise Aura reveals.'),
  exactPair('one_two_three_four', 'hyperfocus', 'synergy', 88, 'One-Two-Three-Four creates more Repair Skill Check opportunities to build Hyperfocus.'),
  exactPair('one_two_three_four', 'scavenger', 'synergy', 84, 'One-Two-Three-Four creates more Repair Skill Checks that can charge Scavenger on Great results.'),
  exactPair('one_two_three_four', 'fast_track', 'synergy', 84, 'One-Two-Three-Four creates more chances to spend Fast Track tokens on a Great Repair Skill Check.'),
  exactPair('one_two_three_four', 'road_life', 'synergy', 84, 'One-Two-Three-Four creates more Repair Skill Checks that can build Road Life on Great results.'),
  exactPair('one_two_three_four', 'specialist', 'synergy', 86, 'One-Two-Three-Four creates more chances to spend Specialist tokens on a Great Repair Skill Check.'),
  exactPair('autodidact', 'deadline', 'synergy', 84, 'While injured, Deadline increases the Healing Skill Checks that Autodidact needs to build tokens.'),
  exactPair('autodidact', 'solidarity', 'synergy', 88, 'Healing another Survivor both builds Autodidact and converts progress into Solidarity self-healing.'),
  exactPair('parental_guidance', 'decisive_strike', 'synergy', 92, 'The Decisive Strike stun immediately triggers Parental Guidance to hide the escape route.'),
  exactPair('parental_guidance', 'last_stand', 'synergy', 90, 'The Last Stand stun triggers Parental Guidance during the fast-vault escape.'),
  exactPair('parental_guidance', 'power_struggle', 'synergy', 90, 'The Power Struggle Pallet stun triggers Parental Guidance after escaping the Killer\'s grasp.'),
  exactPair('parental_guidance', 'blast_mine', 'synergy', 50, 'Blast Mine can trigger Parental Guidance through its Killer stun, but the remote trigger often wastes the nearby escape payoff.'),
  exactPair('parental_guidance', 'head_on', 'synergy', 94, 'The Head On stun triggers Parental Guidance and hides the route out of the locker.'),
  exactPair('parental_guidance', 'smash_hit', 'synergy', 98, 'One Pallet stun triggers both Smash Hit distance and Parental Guidance tracking suppression.'),
  exactPair('last_stand', 'lithe', 'synergy', 88, 'The same fast vault triggers Last Stand\'s stun and Lithe\'s Haste.'),
  exactPair('power_struggle', 'flip_flop', 'synergy', 100, 'Flip-Flop converts recovery into the Wiggle progress required to activate Power Struggle sooner.'),
  exactPair('power_struggle', 'smash_hit', 'synergy', 94, 'The Power Struggle Pallet stun can also trigger Smash Hit for immediate distance.'),
  exactPair('dance_with_me', 'lithe', 'synergy', 100, 'The same fast vault triggers Lithe while Dance With Me hides the departing Scratch Marks.'),
  exactPair('quick_and_quiet', 'lithe', 'synergy', 92, 'Quick & Quiet hides the rushed vault whose Haste is supplied by Lithe.'),
  exactPair('quick_and_quiet', 'head_on', 'synergy', 94, 'Quick & Quiet hides the rushed locker entry used to prepare Head On.'),
  exactPair('vigil', 'balanced_landing', 'synergy', 92, 'Vigil shortens the Exhausted duration applied by Balanced Landing.'),
  exactPair('vigil', 'head_on', 'synergy', 92, 'Vigil shortens the Exhausted duration applied by Head On.'),
  exactPair('vigil', 'lithe', 'synergy', 92, 'Vigil shortens the Exhausted duration applied by Lithe.'),
  exactPair('vigil', 'dead_hard', 'synergy', 92, 'Vigil shortens the Exhausted duration applied by Dead Hard.'),
  exactPair('rapid_response', 'ghost_notes', 'synergy', 94, 'Rapid Response deliberately applies Exhausted, activating Ghost Notes and then recovering it faster.'),
  exactPair('ghost_notes', 'balanced_landing', 'synergy', 84, 'Balanced Landing applies Exhausted, activating Ghost Notes during the escape.'),
  exactPair('ghost_notes', 'head_on', 'synergy', 82, 'Head On applies Exhausted and Ghost Notes helps hide the route after the stun.'),
  exactPair('blood_rush', 'balanced_landing', 'synergy', 80, 'Blood Rush can clear Exhausted after an unhook and enable another Balanced Landing when the map provides a drop.'),
  exactPair('teamwork_throw_down', 'blast_mine', 'synergy', 60, 'Blast Mine blinds the Killer and triggers Teamwork: Throw Down, but its remote timing often wastes part of the chase payoff.'),
  exactPair('teamwork_throw_down', 'flashbang', 'synergy', 90, 'The Flashbang blind is a direct trigger for Teamwork: Throw Down.'),
  exactPair('champion_of_light', 'blast_mine', 'synergy', 62, 'The Blast Mine blind triggers Champion of Light\'s Hindered penalty, though the Survivor is often too far away to convert it.'),
  exactPair('champion_of_light', 'flashbang', 'synergy', 88, 'The Flashbang blind triggers Champion of Light\'s Hindered penalty.'),
  exactPair('residual_manifest', 'blast_mine', 'synergy', 86, 'The Blast Mine blind triggers Residual Manifest\'s Blindness effect.'),
  exactPair('residual_manifest', 'flashbang', 'synergy', 88, 'The Flashbang blind triggers Residual Manifest\'s Blindness effect.'),
  exactPair('residual_manifest', 'streetwise', 'synergy', 86, 'Residual Manifest guarantees a Flashlight from rummaging and Streetwise extends its charges.'),
  exactPair('apocalyptic_ingenuity', 'appraisal', 'synergy', 95, 'Appraisal supplies fast extra rummages for Apocalyptic Ingenuity\'s unlock-or-rummage requirement.'),
  exactPair('apocalyptic_ingenuity', 'plunderers_instinct', 'synergy', 84, 'Plunderer\'s Instinct locates the Chests that Apocalyptic Ingenuity needs to open or rummage.'),
  exactPair('specialist', 'appraisal', 'synergy', 98, 'Every Appraisal rummage supplies a Specialist token, making the chest route substantially more reliable.'),
  exactPair('specialist', 'plunderers_instinct', 'synergy', 84, 'Plunderer\'s Instinct locates the Chests that Specialist needs for tokens.'),
  exactPair('specialist', 'residual_manifest', 'synergy', 88, 'Residual Manifest adds a guaranteed rummage, supplying another Specialist token and a Flashlight.'),
  exactPair('specialist', 'scavenger', 'synergy', 84, 'Great Repair Skill Checks can both spend Specialist tokens and charge Scavenger.'),
  exactPair('appraisal', 'plunderers_instinct', 'synergy', 90, 'Plunderer\'s Instinct locates Chests and improves the item economy that Appraisal extends with rummages.'),
  exactPair('change_of_plan', 'scavenger', 'synergy', 92, 'Scavenger guarantees a Toolbox that Change of Plan can later convert into a full Med-Kit.'),
  exactPair('built_to_last', 'streetwise', 'conditional_synergy', 68, 'Streetwise now boosts only Items retrieved from Chests; Built to Last can refill that same Item later, but brought Items receive no Streetwise value.'),
  exactPair('residual_manifest', 'built_to_last', 'synergy', 88, 'Residual Manifest guarantees a Flashlight from rummaging and Built to Last can refill it after depletion.'),
  exactPair('pharmacy', 'built_to_last', 'conditional_synergy', 82, 'Pharmacy guarantees a rare Med-Kit from a Chest and Built to Last can refill that Med-Kit after it is depleted.'),
  exactPair('residual_manifest', 'champion_of_light', 'synergy', 92, 'Residual Manifest guarantees access to a Flashlight that can trigger Champion of Light.'),
  exactPair('ace_in_the_hole', 'plunderers_instinct', 'synergy', 92, 'Plunderer\'s Instinct improves Chest item rarity while Ace in the Hole adds and preserves Add-ons.'),
  exactPair('saboteur', 'breakout', 'synergy', 94, 'Saboteur identifies and disables the target Hook while Breakout accelerates the carried Survivor\'s Wiggle progress.'),
  exactPair('tenacity', 'unbreakable', 'synergy', 96, 'Tenacity allows crawling while recovering and Unbreakable accelerates and completes that recovery.'),
  exactPair('unbreakable', 'soul_guard', 'synergy', 94, 'Unbreakable provides a reliable self-recovery and Soul Guard protects it with Endurance.'),
  exactPair('no_mither', 'soul_guard', 'synergy', 90, 'No Mither enables repeated full recoveries and Soul Guard grants Endurance after each successful recovery.'),
  exactPair('no_mither', 'resilience', 'synergy', 100, 'No Mither keeps Resilience\'s Injured requirement active for the entire Trial.'),
  exactPair('no_mither', 'invocation_weaving_spiders', 'synergy', 88, 'No Mither has already paid the Invocation\'s Injured and Broken drawback.'),
  exactPair('hardened', 'shoulder_the_burden', 'synergy', 84, 'Shoulder the Burden forces a scream that Hardened suppresses and converts into Killer Aura information.'),
  exactPair('we_see_you', 'object_of_obsession', 'synergy', 98, 'Object of Obsession repeatedly lets the Killer read your Aura, providing a reliable We See You token engine.'),
  exactPair('scene_partner', 'hardened', 'no_effect', 0, 'Hardened prevents the scream that Scene Partner requires, so Scene Partner never triggers.', { bestBuildBlocked: true }),
  exactPair('scene_partner', 'calm_spirit', 'no_effect', 0, 'Calm Spirit prevents the scream that Scene Partner requires, so Scene Partner never triggers.', { bestBuildBlocked: true }),
  exactPair('blood_pact', 'object_of_obsession', 'competition', 0, 'Blood Pact requires not being the Obsession while Object of Obsession requires being the Obsession.', { bestBuildBlocked: true }),
  exactPair('blood_pact', 'for_the_people', 'state_competition', 18, 'For the People makes its user the Obsession and disables Blood Pact after use, but Blood Pact can still provide value beforehand.'),
  exactPair('we_see_you', 'distortion', 'state_competition', 15, 'Distortion prevents the Aura reading that We See You needs while Distortion tokens remain.'),
  exactPair('we_see_you', 'off_the_record', 'state_competition', 10, 'Off the Record blocks the Aura reading that We See You needs during its protection window.'),
  exactPair('finesse', 'resilience', 'state_coverage', 58, 'Finesse covers fast vaults while Healthy and Resilience covers them while Injured; the bonuses never apply simultaneously.'),
  exactPair('resurgence', 'second_wind', 'redundancy', 24, 'Second Wind applies Broken and then heals automatically, leaving Resurgence progress unusable in its successful scenario.'),
  exactPair('resurgence', 'well_make_it', 'independent', 35, 'Resurgence triggers when you are unhooked; We\'ll Make It triggers when you unhook someone else.'),
  exactPair('hyperfocus', 'technician', 'friction', 20, 'Technician neither creates Great Skill Checks nor protects Hyperfocus tokens and increases the progression loss of a miss.'),
  exactPair('teamwork_toughen_up', 'power_struggle', 'independent', 25, 'Toughen Up requires another Survivor\'s blind or Pallet stun; your own Power Struggle does not trigger it.'),
  exactPair('teamwork_toughen_up', 'chemical_trap', 'independent', 24, 'Chemical Trap does not trigger Toughen Up, but both perks remain independently usable on different Pallet events.'),
  exactPair('teamwork_throw_down', 'chemical_trap', 'independent', 24, 'Chemical Trap does not trigger Teamwork: Throw Down, but it does not disable the Teamwork perk either.'),
  exactPair('teamwork_throw_down', 'any_means_necessary', 'indirect', 30, 'Any Means Necessary only resets a Pallet; it does not blind or stun the Killer by itself.'),
  exactPair('fogwise', 'blast_mine', 'theme_only', 28, 'Both involve Generator information, but neither perk activates or extends the other.'),
  exactPair('scavenger', 'built_to_last', 'redundancy', 48, 'Both perks are competing Toolbox recharge engines, so stacking them has diminishing value.')
]

const EXACT_PAIR_RULES = EXACT_PAIR_RULE_LIST.reduce((lookup, entry) => {
  lookup[entry.key] = entry.value
  return lookup
}, {})

function modifierInteraction (modifierId, targetId) {
  if (modifierId === 'eyes_of_belmont') {
    if (typeof TIMED_KILLER_AURA_SOURCES[targetId] === 'number') {
      return interaction(
        'synergy',
        TIMED_KILLER_AURA_SOURCES[targetId],
        'Eyes of Belmont extends this timed Killer Aura reveal by 2 seconds.'
      )
    }
    if (NON_TIMED_AURA_SOURCES.has(targetId)) {
      return interaction(
        'no_effect',
        0,
        'Eyes of Belmont cannot extend this Aura reading because it is not a timed Killer Aura reveal.',
        { bestBuildBlocked: true }
      )
    }
  }

  if (modifierId === 'open_handed') {
    if (typeof FINITE_SURVIVOR_AURA_RANGE_SOURCES[targetId] === 'number') {
      return interaction(
        'synergy',
        FINITE_SURVIVOR_AURA_RANGE_SOURCES[targetId],
        'Open-Handed extends this finite Survivor-based Aura-reading range.'
      )
    }
    if (typeof TIMED_KILLER_AURA_SOURCES[targetId] === 'number' || NON_TIMED_AURA_SOURCES.has(targetId)) {
      return interaction(
        'no_effect',
        0,
        'Open-Handed does not extend this Aura effect because it is not a finite Survivor-based Aura-reading range.',
        { bestBuildBlocked: true }
      )
    }
  }

  return null
}

function exhaustionInteraction (firstId, secondId) {
  const match = ['rapid_response', 'ghost_notes', 'vigil', 'blood_rush']
    .map(modifierId => ({ modifierId, targetId: firstId === modifierId ? secondId : secondId === modifierId ? firstId : null }))
    .find(candidate => candidate.targetId && EXHAUSTION_APPLIERS.has(candidate.targetId))
  if (!match) return null
  const { modifierId, targetId } = match

  if (modifierId === 'rapid_response') {
    return interaction('synergy', 88, 'This perk applies Exhausted and therefore triggers Rapid Response\'s timed Killer Aura reveal.')
  }
  if (modifierId === 'ghost_notes') {
    return interaction('synergy', 84, 'This perk applies Exhausted, activating Ghost Notes\' faster Scratch Mark disappearance and Exhaustion recovery.')
  }
  if (modifierId === 'vigil') {
    return interaction('synergy', 92, 'Vigil shortens the Exhausted duration applied by this perk.')
  }
  if (modifierId === 'blood_rush' && EXHAUSTION_SPENDERS.has(targetId)) {
    return interaction('synergy', 76, 'After an unhook, Blood Rush can clear Exhausted and re-arm this Exhaustion perk for a second situational use.')
  }
  return null
}

export function getSurvivorInteraction (firstPerkOrId, secondPerkOrId) {
  const firstId = canonicalId(firstPerkOrId)
  const secondId = canonicalId(secondPerkOrId)
  if (!firstId || !secondId || firstId === secondId) return null

  const noMitherTarget = firstId === 'no_mither' ? secondId : secondId === 'no_mither' ? firstId : null
  if (noMitherTarget && NO_MITHER_HARD_CONFLICTS[noMitherTarget]) {
    return interaction('anti_synergy', 0, NO_MITHER_HARD_CONFLICTS[noMitherTarget], {
      bestBuildBlocked: true,
      severity: 'hard'
    })
  }

  if (EXHAUSTION_SPENDERS.has(firstId) && EXHAUSTION_SPENDERS.has(secondId)) {
    return interaction(
      'competition',
      0,
      'Both perks spend Exhaustion and cannot be chained as a reliable best-build package.',
      { bestBuildBlocked: true }
    )
  }

  const exact = EXACT_PAIR_RULES[pairKey(firstId, secondId)]
  if (exact) return exact

  const exhaustion = exhaustionInteraction(firstId, secondId)
  if (exhaustion) return exhaustion

  const wideOpenThrottleTarget = firstId === 'wide_open_throttle'
    ? secondId
    : secondId === 'wide_open_throttle'
      ? firstId
      : null
  if (wideOpenThrottleTarget === 'any_means_necessary') {
    return interaction(
      'state_competition',
      28,
      'Wide Open Throttle immediately resets the Pallet it uses, so Any Means Necessary must find a different dropped Pallet.',
      { bestBuildBlocked: false }
    )
  }

  const eyesInteraction = firstId === 'eyes_of_belmont'
    ? modifierInteraction(firstId, secondId)
    : secondId === 'eyes_of_belmont'
      ? modifierInteraction(secondId, firstId)
      : null
  if (eyesInteraction) return eyesInteraction

  const openHandedInteraction = firstId === 'open_handed'
    ? modifierInteraction(firstId, secondId)
    : secondId === 'open_handed'
      ? modifierInteraction(secondId, firstId)
      : null
  if (openHandedInteraction) return openHandedInteraction

  const sprintTarget = firstId === 'sprint_burst' ? secondId : secondId === 'sprint_burst' ? firstId : null
  if (sprintTarget) {
    if (typeof SPRINT_BURST_PACKAGE[sprintTarget] === 'number') {
      return interaction(
        'synergy',
        SPRINT_BURST_PACKAGE[sprintTarget],
        'This perk supports Sprint Burst activation, recovery or route conversion.'
      )
    }
    if (SPRINT_BURST_FALSE_FRIENDS.has(sprintTarget)) {
      return interaction(
        'independent',
        20,
        'This perk is built around repeated vault or rushed-action triggers and does not support Sprint Burst activation or recovery.',
        { bestBuildBlocked: false }
      )
    }
  }

  return null
}

export function getSurvivorInteractionWeight (firstPerkOrId, secondPerkOrId) {
  const rule = getSurvivorInteraction(firstPerkOrId, secondPerkOrId)
  return rule && typeof rule.weight === 'number' ? rule.weight : null
}

export function isSurvivorBestBuildPairBlocked (firstPerkOrId, secondPerkOrId) {
  const rule = getSurvivorInteraction(firstPerkOrId, secondPerkOrId)
  return Boolean(rule && rule.bestBuildBlocked)
}

export function getSurvivorInteractionRoleIds (perkId) {
  const canonical = canonicalId(perkId)
  if (canonical === 'eyes_of_belmont') return ['killer_aura_duration_extension']
  if (typeof TIMED_KILLER_AURA_SOURCES[canonical] === 'number') return ['timed_killer_aura']
  return []
}

export function getSurvivorPerkInteractionKnowledge (perkId) {
  return survivorPerkInteractionKnowledge[canonicalId(perkId)] || null
}
